import * as React from "react";
import { Icon } from "../components/Icon";
import { useDarkMode } from "../general/useDarkMode";
import { Account } from "../account/AccountStore";

/**
 * The top bar: organisation context, theme toggle, and who you are signed in as.
 *
 * ScadsysIdentity has an equivalent (organizations/ContextSwitcher.tsx) but each page renders its own
 * copy as its first child, so the bar re-mounts on every navigation. Here it is mounted once by the
 * shell and pages render only their own content.
 *
 * Organisation switching is a server round trip, not a client-side header write. ScadsysIdentity
 * keeps a chosen organisation in localStorage and sends it as `x-sca-ide-org-context`; this backend
 * derives the tenant from the OIDC session's context claim (and only honours that header for Admins
 * or members of the target org anyway), so switching means re-authenticating with `prompt=select_account`.
 * Every EF global query filter keys off the result, so getting this wrong shows up as an empty app,
 * not an error.
 */

export interface HeaderProps {
    user: Account;
    sidebarOpen: boolean;
    onToggleSidebar: () => void;
}

export const Header: React.FC<HeaderProps> = ({ user, sidebarOpen, onToggleSidebar }) => {
    const [isDarkMode, toggleDarkMode] = useDarkMode();
    const [menuOpen, setMenuOpen] = React.useState(false);
    const menuRef = React.useRef<HTMLDivElement>(null);

    // Close the account menu on an outside click or Escape - a dropdown that only closes by
    // re-clicking its own trigger is a persistent annoyance.
    React.useEffect(() => {
        if (!menuOpen) return;

        const onPointerDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setMenuOpen(false);
        };

        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [menuOpen]);

    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    const switchOrganizationUrl = `/account/signin?select=true&redirectUrl=${returnTo}`;

    return (
        <header className="flex h-[62px] shrink-0 items-center gap-3 border-b border-border bg-surface px-4 lg:px-6">
            <button
                type="button"
                onClick={onToggleSidebar}
                aria-label="Toggle navigation"
                aria-controls="shell-navigation"
                aria-expanded={sidebarOpen}
                className="rounded-btn p-2 text-text-weak transition-colors hover:bg-surface-hover hover:text-text lg:hidden"
            >
                <Icon icon={["fad", "bars"]} />
            </button>

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <Icon icon={["fad", "building"]} className="text-text-weak" />
                    <span className="truncate text-[13px] font-medium text-text">
                        {user.organization?.name ?? "No organisation"}
                    </span>
                </div>
            </div>

            <button
                type="button"
                onClick={() => toggleDarkMode && toggleDarkMode()}
                aria-label={isDarkMode ? "Switch to light theme" : "Switch to dark theme"}
                title={isDarkMode ? "Switch to light theme" : "Switch to dark theme"}
                className="rounded-btn p-2 text-text-weak transition-colors hover:bg-surface-hover hover:text-text"
            >
                <Icon icon={["fad", isDarkMode ? "sun" : "moon"]} />
            </button>

            <div className="relative" ref={menuRef}>
                <button
                    type="button"
                    onClick={() => setMenuOpen((open) => !open)}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    className="flex items-center gap-2.5 rounded-btn px-2 py-1.5 text-[13px] text-text transition-colors hover:bg-surface-muted"
                >
                    <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-on-primary">
                        {initials(user.name)}
                    </span>
                    <span className="hidden max-w-40 truncate sm:inline">{user.name || "Signed out"}</span>
                    <Icon icon={["fad", "chevron-down"]} className="text-xs text-text-weak" />
                </button>

                {menuOpen && (
                    <div
                        role="menu"
                        className="absolute right-0 z-40 mt-1 w-60 overflow-hidden rounded-card border border-border bg-surface shadow-lg"
                    >
                        <div className="border-b border-border px-4 py-3">
                            <div className="truncate text-sm font-medium text-text-strong">{user.name}</div>
                            <div className="truncate text-xs text-text-weak">{user.organization?.name}</div>
                            {user.hasRole("Admin") && <div className="mt-1 text-xs text-primary-text">Administrator</div>}
                        </div>
                        <a href={switchOrganizationUrl} role="menuitem" className="flex items-center gap-2 px-4 py-2.5 text-sm text-text hover:bg-surface-hover">
                            <Icon icon={["fad", "arrow-right-arrow-left"]} fixedWidth className="text-text-weak" />
                            Switch organisation
                        </a>
                        <a href="/account/signout" role="menuitem" className="flex items-center gap-2 px-4 py-2.5 text-sm text-danger-text hover:bg-danger-soft">
                            <Icon icon={["fad", "right-from-bracket"]} fixedWidth />
                            Sign out
                        </a>
                    </div>
                )}
            </div>
        </header>
    );
};

function initials(name?: string): string {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default Header;
